<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Análise de Perdas</v-toolbar-title>
                    <v-spacer/>
                    <PeriodoFilter
                        v-model="periodo"
                        defaultPeriodo="Últimos 6 meses"
                        @update="consultar"
                    />
                    <v-btn icon :loading="loading" title="Atualizar" @click="consultar">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <!-- Filtros -->
                        <v-row align="center" dense>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="filtros.pipeline_id"
                                    :items="pipelines"
                                    item-text="nome"
                                    item-value="id"
                                    label="Pipeline"
                                    outlined
                                    dense
                                    hide-details
                                    clearable
                                    @change="consultar"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="filtros.responsavel_user_id"
                                    :items="usuarios"
                                    item-text="name"
                                    item-value="id"
                                    label="Responsável"
                                    outlined
                                    dense
                                    hide-details
                                    clearable
                                    @change="consultar"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="filtros.motivo_perda_id"
                                    :items="motivosList"
                                    item-text="nome"
                                    item-value="id"
                                    label="Motivo de Perda"
                                    outlined
                                    dense
                                    hide-details
                                    clearable
                                    @change="consultar"
                                />
                            </v-col>
                        </v-row>

                        <!-- KPI Cards -->
                        <v-row>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="String(kpis.total_perdas)"
                                    label="Negócios Perdidos"
                                    color="red darken-2"
                                    icon="mdi-emoticon-sad-outline"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="formatPrice(kpis.valor_total)"
                                    label="Valor Total Perdido"
                                    color="deep-orange darken-1"
                                    icon="mdi-cash-remove"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`${kpis.taxa_perda}%`"
                                    label="Taxa de Perda"
                                    color="orange darken-2"
                                    icon="mdi-percent"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`${kpis.tempo_medio_dias} dias`"
                                    label="Tempo Médio até Perda"
                                    color="blue-grey darken-1"
                                    icon="mdi-clock-alert-outline"
                                />
                            </v-col>
                        </v-row>

                        <!-- Insights -->
                        <v-row v-if="insights.length > 0">
                            <v-col cols="12">
                                <v-card
                                    class="mx-auto"
                                    elevation="4"
                                    outlined
                                    :class="$vuetify.theme.dark ? 'grey darken-3' : 'amber lighten-5'"
                                >
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Insights Estratégicos
                                    </div>
                                    <v-card-text class="pt-0">
                                        <v-row dense>
                                            <v-col
                                                v-for="(insight, i) in insights"
                                                :key="i"
                                                cols="12" sm="6" md="4"
                                            >
                                                <div class="d-flex align-start pa-2 rounded" :class="$vuetify.theme.dark ? 'grey darken-2' : 'white'">
                                                    <v-icon small :color="insight.color" class="mt-1 mr-2 flex-shrink-0">{{ insight.icon }}</v-icon>
                                                    <div>
                                                        <div class="text-caption font-weight-bold" :class="$vuetify.theme.dark ? `${insight.color.split(' ')[0]}--text text--lighten-2` : `${insight.color}--text`">{{ insight.titulo }}</div>
                                                        <div class="text-caption" :class="$vuetify.theme.dark ? 'grey--text text--lighten-1' : 'grey--text text--darken-2'">{{ insight.descricao }}</div>
                                                    </div>
                                                </div>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Gráficos linha 1 -->
                        <v-row>
                            <v-col cols="12" md="8">
                                <v-card class="mx-auto" elevation="4" outlined height="100%">
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Evolução Mensal de Perdas
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartEvolucao" height="110"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-card class="mx-auto" elevation="4" outlined height="100%">
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Por Motivo
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartMotivos" height="200"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Gráficos linha 2 -->
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Perdas por Etapa
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartEtapas" height="150"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Perdas por Responsável
                                    </div>
                                    <v-card-text>
                                        <canvas ref="chartResponsaveis" height="150"></canvas>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Tabela motivos -->
                        <v-row>
                            <v-col cols="12" md="5">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Ranking de Motivos
                                    </div>
                                    <v-data-table
                                        :headers="headersMotivos"
                                        :items="porMotivo"
                                        :mobileBreakpoint="0"
                                        hide-default-footer
                                        :items-per-page="-1"
                                        dense
                                    >
                                        <template v-slot:item.valor="{ item }">
                                            {{ formatPrice(item.valor) }}
                                        </template>
                                        <template v-slot:item.percentual="{ item }">
                                            <div class="d-flex align-center">
                                                <div
                                                    class="mr-2 rounded"
                                                    style="height: 6px; background: #ef5350; border-radius: 3px;"
                                                    :style="{ width: `${item.percentual}%`, maxWidth: '60px' }"
                                                ></div>
                                                {{ item.percentual }}%
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>

                            <!-- Tabela etapas -->
                            <v-col cols="12" md="7">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Perdas por Etapa e Responsável
                                    </div>
                                    <v-row no-gutters>
                                        <v-col cols="12" sm="6">
                                            <v-data-table
                                                :headers="headersEtapa"
                                                :items="porEtapa"
                                                :mobileBreakpoint="0"
                                                hide-default-footer
                                                :items-per-page="-1"
                                                dense
                                            >
                                                <template v-slot:item.valor="{ item }">
                                                    {{ formatPrice(item.valor) }}
                                                </template>
                                                <template v-slot:item.etapa="{ item }">
                                                    <span>
                                                        <span
                                                            v-if="item.cor"
                                                            class="mr-1 d-inline-block rounded-circle"
                                                            style="width:8px;height:8px;vertical-align:middle;"
                                                            :style="{ background: item.cor }"
                                                        ></span>
                                                        {{ item.etapa }}
                                                    </span>
                                                </template>
                                            </v-data-table>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-data-table
                                                :headers="headersResponsavel"
                                                :items="porResponsavel"
                                                :mobileBreakpoint="0"
                                                hide-default-footer
                                                :items-per-page="-1"
                                                dense
                                            >
                                                <template v-slot:item.valor="{ item }">
                                                    {{ formatPrice(item.valor) }}
                                                </template>
                                            </v-data-table>
                                        </v-col>
                                    </v-row>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Tabela detalhada de negócios -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <div class="text-overline subtitle-2 ma-2" style="text-align: center;">
                                        Negócios Perdidos ({{ deals.length }})
                                    </div>
                                    <div class="d-flex justify-center mb-2">
                                        <v-text-field
                                            v-model="searchDeals"
                                            append-icon="mdi-magnify"
                                            label="Buscar"
                                            single-line
                                            hide-details
                                            dense
                                            outlined
                                            style="max-width: 280px;"
                                        />
                                    </div>
                                    <v-data-table
                                        :headers="headersDeals"
                                        :items="deals"
                                        :mobileBreakpoint="0"
                                        :search="searchDeals"
                                        :items-per-page="15"
                                        dense
                                    >
                                        <template v-slot:item.valor="{ item }">
                                            <span class="font-weight-medium">{{ formatPrice(item.valor) }}</span>
                                        </template>
                                        <template v-slot:item.perdido_em="{ item }">
                                            {{ formatDate(item.perdido_em) }}
                                        </template>
                                        <template v-slot:item.motivo_perda="{ item }">
                                            <v-chip x-small color="red" outlined class="font-weight-medium">{{ item.motivo_perda }}</v-chip>
                                        </template>
                                        <template v-slot:item.observacao_perda="{ item }">
                                            <v-tooltip v-if="item.observacao_perda" bottom max-width="260">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon small v-bind="attrs" v-on="on" color="blue">mdi-text-box-outline</v-icon>
                                                </template>
                                                <span style="white-space: pre-line;">{{ item.observacao_perda }}</span>
                                            </v-tooltip>
                                            <span v-else class="grey--text">—</span>
                                        </template>
                                    </v-data-table>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';
import PeriodoFilter from '@/components/PeriodoFilter.vue';
import Chart from 'chart.js';

const PALETTE = [
    '#EF5350', '#EC407A', '#AB47BC', '#7E57C2', '#42A5F5',
    '#26C6DA', '#66BB6A', '#FFA726', '#8D6E63', '#78909C',
];

export default {
    name: 'RelatorioPerdas',
    components: { BaseContainer, Card, PeriodoFilter },

    data: () => ({
        loading: false,
        periodo: {},
        filtros: {
            pipeline_id: null,
            responsavel_user_id: null,
            motivo_perda_id: null,
        },
        pipelines: [],
        usuarios: [],
        motivosList: [],

        // dados do relatório
        kpis: { total_perdas: 0, valor_total: 0, taxa_perda: 0, tempo_medio_dias: 0 },
        porMotivo: [],
        evolucaoMensal: [],
        porEtapa: [],
        porResponsavel: [],
        deals: [],

        searchDeals: '',
        charts: {},

        headersMotivos: [
            { text: 'Motivo', value: 'motivo' },
            { text: 'Qtd', value: 'total', width: 60 },
            { text: 'Valor', value: 'valor' },
            { text: '%', value: 'percentual', width: 100 },
        ],
        headersEtapa: [
            { text: 'Etapa', value: 'etapa' },
            { text: 'Qtd', value: 'total', width: 55 },
            { text: 'Valor', value: 'valor' },
        ],
        headersResponsavel: [
            { text: 'Responsável', value: 'nome' },
            { text: 'Qtd', value: 'total', width: 55 },
            { text: 'Valor', value: 'valor' },
        ],
        headersDeals: [
            { text: 'Título', value: 'titulo' },
            { text: 'Contato', value: 'contato_nome' },
            { text: 'Motivo', value: 'motivo_perda' },
            { text: 'Etapa', value: 'etapa' },
            { text: 'Responsável', value: 'responsavel' },
            { text: 'Valor', value: 'valor' },
            { text: 'Obs.', value: 'observacao_perda', sortable: false },
            { text: 'Perdido em', value: 'perdido_em' },
        ],
    }),

    computed: {
        insights() {
            const list = [];
            if (!this.kpis.total_perdas) return list;

            const top = this.porMotivo[0];
            if (top && top.percentual >= 30) {
                list.push({
                    icon: 'mdi-alert-circle-outline',
                    color: 'red',
                    titulo: `Motivo dominante: ${top.motivo}`,
                    descricao: `Responsável por ${top.percentual}% das perdas. Foque em endereçar este ponto.`,
                });
            }

            // Motivo com maior impacto financeiro (diferente do mais frequente)
            const byValor = [...this.porMotivo].sort((a, b) => b.valor - a.valor);
            if (byValor.length > 0 && byValor[0].motivo !== (top && top.motivo)) {
                list.push({
                    icon: 'mdi-cash-multiple',
                    color: 'deep-orange',
                    titulo: `Maior impacto financeiro: ${byValor[0].motivo}`,
                    descricao: `${this.formatPrice(byValor[0].valor)} em valor perdido neste motivo.`,
                });
            }

            if (this.kpis.taxa_perda >= 50) {
                list.push({
                    icon: 'mdi-trending-down',
                    color: 'red darken-3',
                    titulo: `Taxa de perda crítica: ${this.kpis.taxa_perda}%`,
                    descricao: 'Mais da metade dos negócios fechados estão sendo perdidos. Revise o processo comercial.',
                });
            } else if (this.kpis.taxa_perda >= 30) {
                list.push({
                    icon: 'mdi-alert-outline',
                    color: 'orange',
                    titulo: `Taxa de perda elevada: ${this.kpis.taxa_perda}%`,
                    descricao: 'Taxa acima de 30% merece atenção. Avalie objeções recorrentes.',
                });
            }

            if (this.kpis.tempo_medio_dias >= 30) {
                list.push({
                    icon: 'mdi-clock-alert-outline',
                    color: 'blue',
                    titulo: `Ciclo longo antes da perda: ${this.kpis.tempo_medio_dias} dias`,
                    descricao: 'Negócios ficam muito tempo em aberto antes de ser perdidos. Considere follow-ups mais rápidos.',
                });
            }

            // Etapa com mais perdas
            if (this.porEtapa.length > 0) {
                const topEtapa = this.porEtapa[0];
                list.push({
                    icon: 'mdi-stairs-down',
                    color: 'purple',
                    titulo: `Etapa crítica: ${topEtapa.etapa}`,
                    descricao: `${topEtapa.total} negócio(s) perdido(s) nesta etapa. Avalie os critérios de qualificação.`,
                });
            }

            // Responsável com mais perdas
            if (this.porResponsavel.length > 0 && this.porResponsavel[0].nome !== 'Sem responsável') {
                const topResp = this.porResponsavel[0];
                list.push({
                    icon: 'mdi-account-alert-outline',
                    color: 'teal',
                    titulo: `Mais perdas: ${topResp.nome}`,
                    descricao: `${topResp.total} negócio(s) perdido(s). Pode indicar necessidade de suporte ou treinamento.`,
                });
            }

            return list;
        },
    },

    mounted() {
        this.loadFiltrosBase();
        this.$nextTick(() => this.consultar());
    },

    methods: {
        async loadFiltrosBase() {
            try {
                const [pipelines, usuarios, motivos] = await Promise.all([
                    this.$http.get('crm/pipelines'),
                    this.$http.post('usuarios/get-all', { tipos: ['gestor', 'diretor'] }),
                    this.$http.get('crm/motivos-perda'),
                ]);
                this.pipelines    = pipelines.data.data || [];
                this.usuarios     = usuarios.data.data  || [];
                this.motivosList  = motivos.data.data   || [];
            } catch (e) {
                console.error(e);
            }
        },

        async consultar() {
            this.loading = true;
            try {
                const params = new URLSearchParams();
                if (this.periodo.start) params.append('start', this.periodo.start);
                if (this.periodo.end)   params.append('end',   this.periodo.end);
                if (this.filtros.pipeline_id)       params.append('pipeline_id',       this.filtros.pipeline_id);
                if (this.filtros.responsavel_user_id) params.append('responsavel_user_id', this.filtros.responsavel_user_id);
                if (this.filtros.motivo_perda_id)   params.append('motivo_perda_id',   this.filtros.motivo_perda_id);

                const resp = await this.$http.get(`crm/relatorios/perdas?${params.toString()}`);
                const data = resp.data.data;

                this.kpis           = data.kpis;
                this.porMotivo      = data.por_motivo;
                this.evolucaoMensal = data.evolucao_mensal;
                this.porEtapa       = data.por_etapa;
                this.porResponsavel = data.por_responsavel;
                this.deals          = data.deals;

                this.$nextTick(() => this.gerarGraficos());
            } catch (e) {
                console.error(e);
                this.notify('Erro ao carregar relatório de perdas', 'warning');
            } finally {
                this.loading = false;
            }
        },

        gerarGraficos() {
            // Evolução mensal — barras + linha de valor
            this.renderChart('chartEvolucao', {
                type: 'bar',
                data: {
                    labels: this.evolucaoMensal.map(r => r.mes),
                    datasets: [
                        {
                            label: 'Negócios Perdidos',
                            data: this.evolucaoMensal.map(r => r.total),
                            backgroundColor: 'rgba(239, 83, 80, 0.75)',
                            yAxisID: 'left',
                            order: 2,
                        },
                        {
                            type: 'line',
                            label: 'Valor Perdido (R$)',
                            data: this.evolucaoMensal.map(r => r.valor),
                            borderColor: 'rgba(255, 152, 0, 1)',
                            backgroundColor: 'rgba(255, 152, 0, 0.08)',
                            fill: false,
                            yAxisID: 'right',
                            order: 1,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    scales: {
                        yAxes: [
                            { id: 'left',  position: 'left',  ticks: { beginAtZero: true, precision: 0 } },
                            { id: 'right', position: 'right', gridLines: { drawOnChartArea: false }, ticks: { beginAtZero: true, callback: v => `R$ ${v.toLocaleString('pt-BR')}` } },
                        ],
                    },
                    legend: { position: 'top' },
                },
            });

            // Por motivo — doughnut
            this.renderChart('chartMotivos', {
                type: 'doughnut',
                data: {
                    labels: this.porMotivo.map(r => r.motivo),
                    datasets: [{
                        data: this.porMotivo.map(r => r.total),
                        backgroundColor: this.porMotivo.map((_, i) => PALETTE[i % PALETTE.length]),
                    }],
                },
                options: {
                    responsive: true,
                    cutoutPercentage: 55,
                    legend: { position: 'bottom', labels: { boxWidth: 12 } },
                },
            });

            // Por etapa — barras horizontais
            this.renderChart('chartEtapas', {
                type: 'horizontalBar',
                data: {
                    labels: this.porEtapa.map(r => r.etapa),
                    datasets: [{
                        label: 'Perdas',
                        data: this.porEtapa.map(r => r.total),
                        backgroundColor: this.porEtapa.map(r => r.cor ? `${r.cor}cc` : 'rgba(239,83,80,0.7)'),
                    }],
                },
                options: {
                    responsive: true,
                    legend: { display: false },
                    scales: {
                        xAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
                    },
                },
            });

            // Por responsável — barras horizontais
            this.renderChart('chartResponsaveis', {
                type: 'horizontalBar',
                data: {
                    labels: this.porResponsavel.map(r => r.nome),
                    datasets: [{
                        label: 'Perdas',
                        data: this.porResponsavel.map(r => r.total),
                        backgroundColor: 'rgba(66, 165, 245, 0.75)',
                    }],
                },
                options: {
                    responsive: true,
                    legend: { display: false },
                    scales: {
                        xAxes: [{ ticks: { beginAtZero: true, precision: 0 } }],
                    },
                },
            });
        },

        renderChart(ref, config) {
            if (this.charts[ref]) this.charts[ref].destroy();
            const ctx = this.$refs[ref];
            if (!ctx) return;
            this.charts[ref] = new Chart(ctx, {
                type: config.type,
                data: config.data,
                options: {
                    responsive: true,
                    ...(config.options || {}),
                },
            });
        },

        formatPrice(value) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
        },

        formatDate(value) {
            if (!value) return '';
            return new Date(value).toLocaleDateString('pt-BR');
        },
    },
};
</script>
